import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getTicket, addComment, updateTicket, getOperators, closeTicket, uploadAttachment, deleteTicket, getDepartments, takeTicket } from '../lib/api';
import { statusConfig, priorityConfig, formatDate, formatRelativeDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
    ArrowLeft, 
    Ticket as TicketIcon, 
    Send, 
    Loader2,
    Clock,
    User,
    Tag,
    AlertTriangle,
    Paperclip,
    Download,
    MessageSquare,
    XCircle,
    Lock,
    Upload,
    Trash2,
    Building
} from 'lucide-react';
import { toast } from 'sonner';

export default function TicketDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, isOperator, isAdmin } = useAuth();
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [comment, setComment] = useState('');
    const [commentFile, setCommentFile] = useState(null);
    const [taking, setTaking] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [operators, setOperators] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [updating, setUpdating] = useState(false);
    const [closeDialogOpen, setCloseDialogOpen] = useState(false);
    const [closeReason, setCloseReason] = useState('');
    const [closing, setClosing] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        loadTicket();
        if (isOperator()) {
            loadOperators();
            loadDepartments();
        }
    }, [id]);

    const loadTicket = async () => {
        try {
            const response = await getTicket(id);
            setTicket(response.data);
        } catch (error) {
            console.error('Error loading ticket:', error);
            toast.error('Errore nel caricamento del ticket');
        } finally {
            setLoading(false);
        }
    };

    const loadOperators = async () => {
        try {
            const response = await getOperators();
            setOperators(response.data);
        } catch (error) {
            console.error('Error loading operators:', error);
        }
    };

    const loadDepartments = async () => {
        try {
            const response = await getDepartments();
            setDepartments(response.data);
        } catch (error) {
            console.error('Error loading departments:', error);
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!comment.trim() && !commentFile) return;

        setSubmitting(true);
        try {
            await addComment(id, comment || '(Allegato)', commentFile);
            setComment('');
            setCommentFile(null);
            await loadTicket();
            toast.success(commentFile ? 'Messaggio con allegato inviato' : 'Risposta inviata');
        } catch (error) {
            toast.error('Errore nell\'invio della risposta');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCommentFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File troppo grande (max 5MB)');
            e.target.value = '';
            return;
        }
        setCommentFile(file);
    };

    const removeCommentFile = () => {
        setCommentFile(null);
    };

    const handleUpdateStatus = async (newStatus) => {
        setUpdating(true);
        try {
            await updateTicket(id, { stato: newStatus });
            await loadTicket();
            toast.success('Stato aggiornato');
        } catch (error) {
            toast.error('Errore nell\'aggiornamento');
        } finally {
            setUpdating(false);
        }
    };

    const handleUpdatePriority = async (newPriority) => {
        setUpdating(true);
        try {
            await updateTicket(id, { priorita: newPriority });
            await loadTicket();
            toast.success('Priorità aggiornata');
        } catch (error) {
            toast.error('Errore nell\'aggiornamento');
        } finally {
            setUpdating(false);
        }
    };

    const handleAssignOperator = async (operatorId) => {
        setUpdating(true);
        try {
            await updateTicket(id, { assegnato_a: operatorId || "" });
            await loadTicket();
            toast.success(operatorId ? 'Ticket assegnato' : 'Assegnazione rimossa');
        } catch (error) {
            toast.error('Errore nell\'assegnazione');
        } finally {
            setUpdating(false);
        }
    };

    const handleAssignDepartment = async (deptId) => {
        setUpdating(true);
        try {
            await updateTicket(id, { reparto_id: deptId || "" });
            await loadTicket();
            toast.success(deptId ? 'Reparto assegnato' : 'Reparto rimosso');
        } catch (error) {
            toast.error('Errore nell\'assegnazione reparto');
        } finally {
            setUpdating(false);
        }
    };

    const handleDeleteTicket = async () => {
        setDeleting(true);
        try {
            await deleteTicket(id);
            toast.success('Ticket eliminato');
            navigate('/operator');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
        } finally {
            setDeleting(false);
            setDeleteDialogOpen(false);
        }
    };

    const handleTakeTicket = async () => {
        setTaking(true);
        try {
            await takeTicket(id);
            await loadTicket();
            toast.success('Ticket preso in carico');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nella presa in carico');
        } finally {
            setTaking(false);
        }
    };

    const handleCloseTicket = async () => {
        if (!closeReason.trim()) {
            toast.error('Inserisci una motivazione per la chiusura');
            return;
        }

        setClosing(true);
        try {
            await closeTicket(id, closeReason);
            await loadTicket();
            toast.success('Ticket chiuso con successo');
            setCloseDialogOpen(false);
            setCloseReason('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nella chiusura del ticket');
        } finally {
            setClosing(false);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            toast.error('File troppo grande (max 5MB)');
            return;
        }

        setUploading(true);
        try {
            await uploadAttachment(id, file);
            await loadTicket();
            toast.success('Allegato caricato');
        } catch (error) {
            toast.error('Errore nel caricamento del file');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const downloadAttachment = (attachment) => {
        const link = document.createElement('a');
        link.href = `data:${attachment.tipo};base64,${attachment.data}`;
        link.download = attachment.nome;
        link.click();
    };

    const getBackUrl = () => {
        return isOperator() ? '/operator' : '/dashboard';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (!ticket) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <Card className="w-full max-w-md">
                    <CardContent className="pt-6 text-center">
                        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-slate-900 mb-2">Ticket non trovato</h2>
                        <Button onClick={() => navigate(getBackUrl())}>Torna alla Dashboard</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center h-16">
                        <Button 
                            variant="ghost" 
                            onClick={() => navigate(getBackUrl())}
                            className="mr-4"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Indietro
                        </Button>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center">
                                <TicketIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <code className="text-sm font-mono text-slate-500">{ticket.numero_ticket}</code>
                                <h1 className="text-lg font-bold text-slate-900 truncate max-w-md">{ticket.oggetto}</h1>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Ticket Description */}
                        <Card className="border-slate-200 animate-fade-in">
                            <CardHeader>
                                <CardTitle className="text-lg">Descrizione</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-slate-700 whitespace-pre-wrap">{ticket.descrizione}</p>
                                
                                {/* Attachments */}
                                <div className="mt-6">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-sm font-medium text-slate-900 flex items-center gap-2">
                                            <Paperclip className="w-4 h-4" />
                                            Allegati ({ticket.allegati?.length || 0})
                                        </h4>
                                        {ticket.stato !== 'Chiuso' && (
                                            <div>
                                                <input
                                                    type="file"
                                                    id="attachment-upload"
                                                    className="hidden"
                                                    onChange={handleFileUpload}
                                                    disabled={uploading}
                                                />
                                                <label htmlFor="attachment-upload">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="cursor-pointer"
                                                        disabled={uploading}
                                                        asChild
                                                    >
                                                        <span>
                                                            {uploading ? (
                                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                            ) : (
                                                                <Upload className="w-4 h-4 mr-2" />
                                                            )}
                                                            Carica File
                                                        </span>
                                                    </Button>
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                    {ticket.allegati?.length > 0 ? (
                                        <div className="space-y-2">
                                            {ticket.allegati.map((file) => (
                                                <div 
                                                    key={file.id}
                                                    className="flex items-center justify-between bg-slate-50 rounded-lg p-3"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <Paperclip className="w-4 h-4 text-slate-400" />
                                                        <span className="text-sm text-slate-700">{file.nome}</span>
                                                        <span className="text-xs text-slate-400">
                                                            ({(file.dimensione / 1024).toFixed(1)} KB)
                                                        </span>
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => downloadAttachment(file)}
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded-lg">
                                            Nessun allegato
                                        </p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Comments */}
                        <Card className="border-slate-200">
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <MessageSquare className="w-5 h-5" />
                                    Conversazione ({ticket.commenti?.length || 0})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {/* Comments List */}
                                <div className="space-y-4 mb-6">
                                    {ticket.commenti?.length === 0 ? (
                                        <p className="text-center text-slate-500 py-8">
                                            Nessuna risposta ancora. Sii il primo a rispondere!
                                        </p>
                                    ) : (
                                        ticket.commenti?.map((comment, index) => (
                                            <div 
                                                key={comment.id} 
                                                className={`flex gap-3 animate-slide-in ${
                                                    comment.autore_ruolo !== 'cliente' ? 'flex-row-reverse' : ''
                                                }`}
                                                style={{ animationDelay: `${index * 50}ms` }}
                                            >
                                                <Avatar className="w-8 h-8 flex-shrink-0">
                                                    <AvatarFallback className={
                                                        comment.autore_ruolo !== 'cliente' 
                                                            ? 'bg-indigo-100 text-indigo-700' 
                                                            : 'bg-slate-100 text-slate-700'
                                                    }>
                                                        {comment.autore_nome.split(' ').map(n => n[0]).join('')}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className={`flex-1 ${
                                                    comment.autore_ruolo !== 'cliente' ? 'text-right' : ''
                                                }`}>
                                                    <div className={`inline-block rounded-lg p-4 max-w-[85%] ${
                                                        comment.autore_ruolo !== 'cliente' 
                                                            ? 'bg-indigo-500 text-white' 
                                                            : 'bg-slate-100 text-slate-900'
                                                    }`}>
                                                        <p className="text-sm whitespace-pre-wrap">{comment.contenuto}</p>
                                                        {comment.allegato && (
                                                            <div 
                                                                className={`mt-2 pt-2 border-t ${
                                                                    comment.autore_ruolo !== 'cliente' 
                                                                        ? 'border-indigo-400' 
                                                                        : 'border-slate-200'
                                                                }`}
                                                            >
                                                                <button
                                                                    onClick={() => downloadAttachment(comment.allegato)}
                                                                    className={`flex items-center gap-2 text-xs hover:underline ${
                                                                        comment.autore_ruolo !== 'cliente' 
                                                                            ? 'text-indigo-100' 
                                                                            : 'text-indigo-600'
                                                                    }`}
                                                                >
                                                                    <Paperclip className="w-3 h-3" />
                                                                    <span>{comment.allegato.nome}</span>
                                                                    <span className="opacity-70">
                                                                        ({(comment.allegato.dimensione / 1024).toFixed(1)} KB)
                                                                    </span>
                                                                    <Download className="w-3 h-3" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className={`mt-1 text-xs text-slate-400 ${
                                                        comment.autore_ruolo !== 'cliente' ? 'text-right' : ''
                                                    }`}>
                                                        <span className="font-medium">{comment.autore_nome}</span>
                                                        {comment.autore_ruolo !== 'cliente' && (
                                                            <Badge variant="outline" className="ml-2 text-xs">
                                                                {comment.autore_ruolo === 'admin' ? 'Admin' : 'Operatore'}
                                                            </Badge>
                                                        )}
                                                        {comment.tipo === 'chiusura' && (
                                                            <Badge variant="outline" className="ml-2 text-xs bg-slate-100 text-slate-600">
                                                                <Lock className="w-3 h-3 mr-1" />
                                                                Chiusura
                                                            </Badge>
                                                        )}
                                                        <span className="ml-2">{formatRelativeDate(comment.created_at)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>

                                <Separator className="my-6" />

                                {/* Add Comment Form */}
                                {ticket.stato !== 'Chiuso' ? (
                                    <form onSubmit={handleAddComment}>
                                        <Label htmlFor="comment">Aggiungi Risposta</Label>
                                        <Textarea
                                            id="comment"
                                            placeholder="Scrivi la tua risposta..."
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                            rows={3}
                                            className="mt-2"
                                            data-testid="comment-textarea"
                                        />
                                        
                                        {/* File attachment for comment */}
                                        {commentFile ? (
                                            <div className="mt-3 p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <Paperclip className="w-4 h-4 text-indigo-500" />
                                                    <span className="text-sm text-slate-700">{commentFile.name}</span>
                                                    <span className="text-xs text-slate-400">
                                                        ({(commentFile.size / 1024).toFixed(1)} KB)
                                                    </span>
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={removeCommentFile}
                                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                >
                                                    <XCircle className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        ) : null}
                                        
                                        <div className="flex justify-between items-center mt-3">
                                            <div>
                                                <input
                                                    type="file"
                                                    id="comment-file-upload"
                                                    className="hidden"
                                                    onChange={handleCommentFileSelect}
                                                    disabled={submitting}
                                                />
                                                <label htmlFor="comment-file-upload">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="cursor-pointer"
                                                        disabled={submitting}
                                                        asChild
                                                    >
                                                        <span>
                                                            <Paperclip className="w-4 h-4 mr-2" />
                                                            Allega File
                                                        </span>
                                                    </Button>
                                                </label>
                                            </div>
                                            <Button 
                                                type="submit" 
                                                disabled={submitting || (!comment.trim() && !commentFile)}
                                                className="bg-indigo-500 hover:bg-indigo-600"
                                                data-testid="send-comment-btn"
                                            >
                                                {submitting ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : (
                                                    <Send className="w-4 h-4 mr-2" />
                                                )}
                                                Invia
                                            </Button>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="text-center py-4 bg-slate-50 rounded-lg">
                                        <p className="text-slate-500">Questo ticket è chiuso. Non è possibile aggiungere risposte.</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Status Card */}
                        <Card className="border-slate-200 animate-fade-in">
                            <CardHeader>
                                <CardTitle className="text-lg">Dettagli</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Status */}
                                <div>
                                    <Label className="text-xs text-slate-500">Stato</Label>
                                    {isOperator() ? (
                                        <>
                                            <Select 
                                                value={ticket.stato} 
                                                onValueChange={handleUpdateStatus}
                                                disabled={updating}
                                            >
                                                <SelectTrigger className="mt-1" data-testid="status-select">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="Aperto">Aperto</SelectItem>
                                                    <SelectItem value="In attesa risposta">In attesa risposta</SelectItem>
                                                    <SelectItem value="In lavorazione">In lavorazione</SelectItem>
                                                    <SelectItem value="Risolto">Risolto</SelectItem>
                                                    <SelectItem value="Chiuso">Chiuso</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            {ticket.stato !== 'Chiuso' && !ticket.assegnato_a && isOperator() && (
                                                <Button
                                                    variant="default"
                                                    size="sm"
                                                    onClick={handleTakeTicket}
                                                    disabled={taking}
                                                    className="mt-2 w-full bg-indigo-500 hover:bg-indigo-600"
                                                    data-testid="take-ticket-btn"
                                                >
                                                    {taking ? (
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    ) : (
                                                        <User className="w-4 h-4 mr-2" />
                                                    )}
                                                    Prendi in Carico
                                                </Button>
                                            )}
                                            {ticket.stato !== 'Chiuso' && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setCloseDialogOpen(true)}
                                                    className="mt-2 w-full text-red-600 border-red-200 hover:bg-red-50"
                                                    data-testid="close-ticket-btn"
                                                >
                                                    <XCircle className="w-4 h-4 mr-2" />
                                                    Chiudi Ticket
                                                </Button>
                                            )}
                                        </>
                                    ) : (
                                        <Badge 
                                            className={`mt-1 ${statusConfig[ticket.stato]?.bg} ${statusConfig[ticket.stato]?.text} ${statusConfig[ticket.stato]?.border}`}
                                        >
                                            {ticket.stato}
                                        </Badge>
                                    )}
                                </div>

                                {/* Priority */}
                                <div>
                                    <Label className="text-xs text-slate-500">Priorità</Label>
                                    {isOperator() ? (
                                        <Select 
                                            value={ticket.priorita} 
                                            onValueChange={handleUpdatePriority}
                                            disabled={updating}
                                        >
                                            <SelectTrigger className="mt-1" data-testid="priority-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Bassa">Bassa</SelectItem>
                                                <SelectItem value="Media">Media</SelectItem>
                                                <SelectItem value="Alta">Alta</SelectItem>
                                                <SelectItem value="Urgente">Urgente</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <Badge 
                                            className={`mt-1 ${priorityConfig[ticket.priorita]?.bg} ${priorityConfig[ticket.priorita]?.text}`}
                                        >
                                            {ticket.priorita}
                                        </Badge>
                                    )}
                                </div>

                                <Separator />

                                {/* Category */}
                                <div className="flex items-center gap-2">
                                    <Tag className="w-4 h-4 text-slate-400" />
                                    <div>
                                        <Label className="text-xs text-slate-500">Categoria</Label>
                                        <p className="text-sm text-slate-900">{ticket.categoria}</p>
                                    </div>
                                </div>

                                {/* Created */}
                                <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-slate-400" />
                                    <div>
                                        <Label className="text-xs text-slate-500">Creato</Label>
                                        <p className="text-sm text-slate-900">{formatDate(ticket.created_at)}</p>
                                    </div>
                                </div>

                                {/* Updated */}
                                <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-slate-400" />
                                    <div>
                                        <Label className="text-xs text-slate-500">Ultimo aggiornamento</Label>
                                        <p className="text-sm text-slate-900">{formatRelativeDate(ticket.updated_at)}</p>
                                    </div>
                                </div>

                                <Separator />

                                {/* Client */}
                                <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-slate-400" />
                                    <div>
                                        <Label className="text-xs text-slate-500">Cliente</Label>
                                        <p className="text-sm text-slate-900">{ticket.cliente_nome}</p>
                                        <p className="text-xs text-slate-500">{ticket.cliente_email}</p>
                                        {ticket.cliente_societa && (
                                            <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                                                <Building className="w-3 h-3" />
                                                {ticket.cliente_societa}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Department Assignment (for operators only) */}
                                {isOperator() && (
                                    <div>
                                        <Label className="text-xs text-slate-500">Reparto</Label>
                                        <Select 
                                            value={ticket.reparto_id || '__none__'} 
                                            onValueChange={(val) => handleAssignDepartment(val === '__none__' ? '' : val)}
                                            disabled={updating}
                                        >
                                            <SelectTrigger className="mt-1" data-testid="department-select">
                                                <SelectValue placeholder="Nessun reparto" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__none__">Nessun reparto</SelectItem>
                                                {departments.map((dept) => (
                                                    <SelectItem key={dept.id} value={dept.id}>
                                                        {dept.nome}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {/* Assigned Operator (for operators only) */}
                                {isOperator() && (
                                    <div>
                                        <Label className="text-xs text-slate-500">Assegnato a</Label>
                                        <Select 
                                            value={ticket.assegnato_a || '__none__'} 
                                            onValueChange={(val) => handleAssignOperator(val === '__none__' ? '' : val)}
                                            disabled={updating}
                                        >
                                            <SelectTrigger className="mt-1" data-testid="assign-select">
                                                <SelectValue placeholder="Non assegnato" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__none__">Non assegnato</SelectItem>
                                                {operators.map((op) => (
                                                    <SelectItem key={op.id} value={op.id}>
                                                        {op.nome} {op.cognome}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {/* Delete Ticket (admin only) */}
                                {isAdmin && isAdmin() && (
                                    <div className="pt-4 border-t">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                            onClick={() => setDeleteDialogOpen(true)}
                                            data-testid="delete-ticket-btn"
                                        >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Elimina Ticket
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Closure Info */}
                        {ticket.stato === 'Chiuso' && ticket.motivazione_chiusura && (
                            <Card className="border-slate-200 border-l-4 border-l-slate-400 animate-fade-in">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm flex items-center gap-2 text-slate-600">
                                        <Lock className="w-4 h-4" />
                                        Informazioni Chiusura
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2 text-sm">
                                    <div>
                                        <Label className="text-xs text-slate-500">Motivazione</Label>
                                        <p className="text-slate-700">{ticket.motivazione_chiusura}</p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-slate-500">Chiuso da</Label>
                                        <p className="text-slate-700">{ticket.chiuso_da}</p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-slate-500">Data chiusura</Label>
                                        <p className="text-slate-700">{formatDate(ticket.chiuso_il)}</p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>

                {/* Close Ticket Dialog */}
                <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <XCircle className="w-5 h-5 text-red-500" />
                                Chiudi Ticket
                            </DialogTitle>
                            <DialogDescription>
                                Stai per chiudere il ticket <strong>{ticket?.numero_ticket}</strong>. 
                                Inserisci una motivazione per la chiusura.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="closeReason">Motivazione *</Label>
                                <Textarea
                                    id="closeReason"
                                    placeholder="Descrivi il motivo della chiusura del ticket..."
                                    value={closeReason}
                                    onChange={(e) => setCloseReason(e.target.value)}
                                    rows={4}
                                    data-testid="close-reason-textarea"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={() => {
                                    setCloseDialogOpen(false);
                                    setCloseReason('');
                                }}
                            >
                                Annulla
                            </Button>
                            <Button 
                                onClick={handleCloseTicket}
                                disabled={closing || !closeReason.trim()}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid="confirm-close-btn"
                            >
                                {closing ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Chiusura...
                                    </>
                                ) : (
                                    <>
                                        <XCircle className="w-4 h-4 mr-2" />
                                        Chiudi Ticket
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Delete Ticket Dialog */}
                <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare questo ticket?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Stai per eliminare definitivamente il ticket <strong>{ticket?.numero_ticket}</strong>. 
                                Questa azione non può essere annullata. Tutti i commenti e gli allegati verranno persi.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleDeleteTicket}
                                className="bg-red-500 hover:bg-red-600"
                                disabled={deleting}
                                data-testid="confirm-delete-ticket-btn"
                            >
                                {deleting ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Trash2 className="w-4 h-4 mr-2" />
                                )}
                                Elimina
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </main>
        </div>
    );
}
